import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, IconButton, Typography, Grid, Divider,
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody
} from '@mui/material';
import { Close as CloseIcon, Print as PrintIcon } from '@mui/icons-material';
import { formatCurrency, formatAddressFromFields, formatCompanyAddress } from './utils';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  if (typeof dateString === 'string' && dateString.includes('/')) return dateString;
  if (typeof dateString === 'string' && dateString.includes('-')) {
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  }
  const date = new Date(dateString + 'T12:00:00');
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
};

const PrintPreview = ({ open, onClose, invoice, lineItems, company, customers, onPrint }) => {
  const customer = (customers || []).find(c => c.id === invoice.customer_id);
  const companyAddress = formatCompanyAddress(company);
  const billToAddress = formatAddressFromFields(
    invoice.billing_street, invoice.billing_city, invoice.billing_state,
    invoice.billing_zip, invoice.billing_country
  );
  const shipToAddress = formatAddressFromFields(
    invoice.shipping_street, invoice.shipping_city, invoice.shipping_state,
    invoice.shipping_zip, invoice.shipping_country
  );

  const validItems = (lineItems || []).filter(item => item.product_id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth disableScrollLock>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 500, color: 'primary.main' }}>
        <Typography variant="h6">Invoice Preview</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <div style={{ padding: '24px', backgroundColor: '#fff' }}>

          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
            <Box>
              <Typography variant="h5" sx={{ mb: 1 }}>INVOICE</Typography>
              <Typography variant="body2" color="text.secondary">Invoice #: {invoice.invoice_number || 'N/A'}</Typography>
              <Typography variant="body2" color="text.secondary">Date: {formatDate(invoice.invoice_date)}</Typography>
              <Typography variant="body2" color="text.secondary">Due: {formatDate(invoice.due_date)}</Typography>
              {invoice.purchase_order_number && (
                <Typography variant="body2" color="text.secondary">PI Ref: {invoice.purchase_order_number}</Typography>
              )}
              {invoice.bill_of_lading_number && (
                <Typography variant="body2" color="text.secondary">BOL Ref: {invoice.bill_of_lading_number}</Typography>
              )}
              {invoice.status && (
                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, textTransform: 'uppercase', color: invoice.status === 'paid' ? 'success.main' : invoice.status === 'overdue' ? 'error.main' : 'primary.main' }}>
                  {invoice.status}
                </Typography>
              )}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6">{company?.name || 'Your Company Name'}</Typography>
              {companyAddress && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{companyAddress}</Typography>
              )}
              {company?.phone && <Typography variant="body2">Phone: {company.phone}</Typography>}
              {company?.email && <Typography variant="body2">Email: {company.email}</Typography>}
              {company?.website && <Typography variant="body2">Web: {company.website}</Typography>}
            </Box>
          </Box>

          {/* Bill To / Ship To */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Bill To:</Typography>
              {invoice.customer_name && <Typography variant="body2">{invoice.customer_name}</Typography>}
              {(invoice.customer_company || customer?.company) && <Typography variant="body2">{invoice.customer_company || customer?.company}</Typography>}
              {customer?.phone && <Typography variant="body2">Phone: {customer.phone}</Typography>}
              {customer?.email && <Typography variant="body2">Email: {customer.email}</Typography>}
              {billToAddress && (
                <>
                  <Typography variant="body2" sx={{ mt: 2 }}>Address:</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{billToAddress}</Typography>
                </>
              )}
            </Grid>
            {shipToAddress && (
              <Grid item xs={6}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Ship To:</Typography>
                {invoice.customer_name && <Typography variant="body2">{invoice.customer_name}</Typography>}
                {(invoice.customer_company || customer?.company) && <Typography variant="body2">{invoice.customer_company || customer?.company}</Typography>}
                {customer?.phone && <Typography variant="body2">Phone: {customer.phone}</Typography>}
                {customer?.email && <Typography variant="body2">Email: {customer.email}</Typography>}
                <Typography variant="body2" sx={{ mt: 2 }}>Address:</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{shipToAddress}</Typography>
              </Grid>
            )}
          </Grid>

          {/* Order Summary Table */}
          <Box sx={{ mb: 2, mt: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, pb: 1, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
              Order Summary
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: 'auto', borderRadius: 3, backgroundColor: '#fff', border: '1px solid rgba(0,0,0,0.12)', mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Size</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>UoM</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {validItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" color="text.secondary">No line items</Typography>
                    </TableCell>
                  </TableRow>
                ) : validItems.map((item, idx) => {
                  const qty = parseFloat(item.quantity) || 0;
                  const qtyLabel = item.unit_type === 'Unit'
                    ? `${qty} ${qty === 1 ? 'Unit' : 'Units'}`
                    : `${qty} ${qty === 1 ? 'Case' : 'Cases'}`;
                  return (
                    <TableRow key={idx}>
                      <TableCell><Typography variant="body2">{item.product_name || '—'}</Typography></TableCell>
                      <TableCell align="center"><Typography variant="body2">{item.variant_name || '-'}</Typography></TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {item.unit_type !== 'Unit' && item.units_per_case ? `${item.units_per_case}U/C` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{qtyLabel}</TableCell>
                      <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.line_total || item.total_price)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totals */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Box sx={{ width: '300px', mt: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}><Typography>Subtotal:</Typography></Grid>
                <Grid item xs={6} textAlign="right"><Typography>{formatCurrency(invoice.subtotal)}</Typography></Grid>

                {parseFloat(invoice.discount) > 0 && (
                  <>
                    <Grid item xs={6}><Typography>Discount ({invoice.discount}%):</Typography></Grid>
                    <Grid item xs={6} textAlign="right"><Typography>-{formatCurrency(invoice.discount_amount)}</Typography></Grid>
                  </>
                )}

                {parseFloat(invoice.tax_amount) > 0 && (
                  <>
                    <Grid item xs={6}><Typography>Tax ({invoice.tax_rate}%):</Typography></Grid>
                    <Grid item xs={6} textAlign="right"><Typography>+{formatCurrency(invoice.tax_amount)}</Typography></Grid>
                  </>
                )}

                {parseFloat(invoice.shipping_fee) > 0 && (
                  <>
                    <Grid item xs={6}><Typography>Shipping:</Typography></Grid>
                    <Grid item xs={6} textAlign="right"><Typography>+{formatCurrency(invoice.shipping_fee)}</Typography></Grid>
                  </>
                )}

                {parseFloat(invoice.handling_fee) > 0 && (
                  <>
                    <Grid item xs={6}><Typography>Handling:</Typography></Grid>
                    <Grid item xs={6} textAlign="right"><Typography>+{formatCurrency(invoice.handling_fee)}</Typography></Grid>
                  </>
                )}

                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

                <Grid item xs={6}><Typography fontWeight="bold">Total:</Typography></Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography fontWeight="bold" color="primary">{formatCurrency(invoice.total_before_credit)}</Typography>
                </Grid>

                {parseFloat(invoice.credit_applied) > 0 && (
                  <>
                    <Grid item xs={6}><Typography color="success.main">Credit Applied:</Typography></Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography color="success.main">-{formatCurrency(invoice.credit_applied)}</Typography>
                    </Grid>
                    <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
                    <Grid item xs={6}><Typography fontWeight="bold">Amount Due:</Typography></Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography fontWeight="bold" color="primary">{formatCurrency(invoice.amount_due)}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Box>

          {/* Payment Terms */}
          {(invoice.payment_terms || invoice.payment_method || parseFloat(invoice.late_fee_rate) > 0 || parseFloat(invoice.early_payment_discount) > 0 || parseInt(invoice.early_payment_days) > 0) && (
            <Box sx={{ mt: 3 }}>
              <Typography fontWeight="bold" gutterBottom>Payment Terms:</Typography>
              {invoice.payment_terms && <Typography variant="body2">Payment Due: {invoice.payment_terms}</Typography>}
              {invoice.payment_method && <Typography variant="body2">Preferred Method: {invoice.payment_method}</Typography>}
              {(parseFloat(invoice.early_payment_discount) > 0 || parseInt(invoice.early_payment_days) > 0) && (
                <Typography variant="body2">
                  Early Payment: {parseFloat(invoice.early_payment_discount) > 0 ? `${invoice.early_payment_discount}% discount if paid within ` : 'Due within '}{invoice.early_payment_days || 0} days
                </Typography>
              )}
              {parseFloat(invoice.late_fee_rate) > 0 && (
                <Typography variant="body2">Late Fee: {invoice.late_fee_rate}% per month on overdue balances</Typography>
              )}
            </Box>
          )}

          {/* Notes */}
          {invoice.notes && (
            <Box sx={{ mt: 3 }}>
              <Typography fontWeight="bold" gutterBottom>Notes:</Typography>
              <Typography sx={{ whiteSpace: 'pre-line' }}>{invoice.notes}</Typography>
            </Box>
          )}

          {/* Special Instructions */}
          {invoice.special_instructions && (
            <Box sx={{ mt: 2 }}>
              <Typography fontWeight="bold" gutterBottom>Special Instructions:</Typography>
              <Typography sx={{ whiteSpace: 'pre-line' }}>{invoice.special_instructions}</Typography>
            </Box>
          )}

          {/* Terms & Conditions */}
          {invoice.terms_and_conditions && (
            <Box sx={{ mt: 3, mb: 2, p: 2, border: '1px solid #000' }}>
              <Typography fontWeight="bold" gutterBottom sx={{ mb: 1 }}>Terms & Conditions</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '0.75rem', lineHeight: 1.4 }}>
                {invoice.terms_and_conditions}
              </Typography>
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Thank you for your business!</Typography>
            </Box>
            {invoice.prepared_by?.name && (
              <Box sx={{ textAlign: 'right', minWidth: '250px' }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>Prepared by:</Typography>
                <Box sx={{ fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", fontSize: '18pt', color: '#000', mb: 1 }}>
                  {invoice.prepared_by.name}
                </Box>
                <Typography variant="body2" color="text.secondary">{invoice.prepared_by.position || 'Sales Representative'}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{formatDate(invoice.prepared_by.date)}</Typography>
              </Box>
            )}
          </Box>

        </div>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={onPrint} sx={{ borderRadius: 2 }}>Print / Download</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintPreview;
